/**
 * SiteEvidenceControlPanel — unified Site Evidence mode + actions surface.
 *
 * Combines EvidenceModeControl and the primary SED lifecycle toolbar into
 * one bordered card. Secondary actions (Google Earth, layers, KMZ upload,
 * Retry on failed) live in the overflow menu.
 */
import React from 'react';
import { AlertTriangle, Download, RefreshCw, Trash2 } from 'lucide-react';
import type { SiteEntity } from '../../types/site';
import { siteRequiresLayerEvidenceRebuild } from '../../services/siteLayers/evidencePolicy';
import { EvidenceModeControl } from './EvidenceModeControl';
import { SiteEvidenceActionsMenu } from './SiteEvidenceActionsMenu';
import type { SedStatus } from './SiteEvidenceToolbar';

export interface SiteEvidenceControlPanelProps {
  site: SiteEntity;
  sedStatus: SedStatus;
  hasGeometry: boolean;
  isReloading?: boolean;
  isRebuilding?: boolean;
  isDeletingSed?: boolean;
  onOpenRequery?: () => void;
  onReloadSed?: () => void;
  onRebuildSed?: () => void;
  onDownloadSed?: () => void;
  onExportKMZ?: () => void;
  onUpdateKMZ?: () => void;
  onOpenGoogleEarth?: () => void;
  onEditLayers?: () => void;
  onDeleteSed?: () => void;
  onRetrySed?: () => void;
  'data-testid'?: string;
}

interface ActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  titleAttr?: string;
  testId: string;
  tone?: 'default' | 'danger';
  spinning?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  icon,
  onClick,
  disabled = false,
  titleAttr,
  testId,
  tone = 'default',
  spinning = false,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || !onClick}
    title={titleAttr}
    data-testid={testId}
    className={`flex min-h-[88px] flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
      tone === 'danger'
        ? 'border-danger/30 bg-background hover:bg-danger/5'
        : 'border-border bg-background hover:bg-muted/60'
    }`}
  >
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${
      tone === 'danger' ? 'text-danger' : 'text-text-primary'
    }`}>
      <span className={spinning ? 'animate-spin' : undefined}>{icon}</span>
      {title}
    </span>
    <span className="text-[10px] text-text-muted leading-snug">{description}</span>
  </button>
);

export const SiteEvidenceControlPanel: React.FC<SiteEvidenceControlPanelProps> = ({
  site,
  sedStatus,
  hasGeometry,
  isReloading = false,
  isRebuilding = false,
  isDeletingSed = false,
  onOpenRequery,
  onReloadSed,
  onRebuildSed,
  onDownloadSed,
  onExportKMZ,
  onUpdateKMZ,
  onOpenGoogleEarth,
  onEditLayers,
  onDeleteSed,
  onRetrySed,
  'data-testid': testId = 'site-evidence-control-panel',
}) => {
  const rebuildActive = isRebuilding || sedStatus === 'bootstrapping';
  const reloadSpinning = isReloading && !rebuildActive;
  const reloadDisabled = isReloading || rebuildActive || !site.sedId;
  const rebuildDisabled = isRebuilding || sedStatus === 'bootstrapping';
  const showRequery = sedStatus !== 'bootstrapping' && sedStatus !== 'failed' && Boolean(onOpenRequery);
  const layerPolicyRebuildRecommended = Boolean(site.sedId)
    && siteRequiresLayerEvidenceRebuild(site.siteLayers);

  return (
    <div
      className="rounded-md border border-border-subtle bg-surface-1/50 p-4 space-y-4"
      data-testid={testId}
    >
      <EvidenceModeControl site={site} />
      {layerPolicyRebuildRecommended && (
        <div
          role="status"
          data-testid={`${testId}-layer-policy-rebuild-warning`}
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[11px] text-text-secondary"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          <span>
            Report rebuild recommended: one or more map layers now have screening or map-only evidence status.
          </span>
        </div>
      )}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 pt-1 border-t border-border-subtle"
        data-testid={`${testId}-toolbar`}
      >
        {showRequery && (
          <ActionCard
            title="Re-query providers"
            description="Refresh provider APIs for this site without a full rebuild."
            icon={<RefreshCw className="w-3.5 h-3.5" aria-hidden />}
            onClick={onOpenRequery}
            testId={`${testId}-requery`}
          />
        )}
        {onReloadSed && (
          <ActionCard
            title="Reload SED"
            description="Load the latest SED JSON from storage (no provider re-query)."
            icon={<RefreshCw className="w-3.5 h-3.5" aria-hidden />}
            onClick={onReloadSed}
            disabled={reloadDisabled}
            titleAttr={
              rebuildActive
                ? 'Rebuild in progress — cached reload is meaningless until it finishes.'
                : undefined
            }
            testId={`${testId}-reload`}
            spinning={reloadSpinning}
          />
        )}
        {onRebuildSed && site.sedId && (
          <ActionCard
            title={sedStatus === 'bootstrapping' ? 'Rebuilding…' : 'Rebuild SED'}
            description="Re-run the bootstrap pipeline and bump the SED version."
            icon={<RefreshCw className="w-3.5 h-3.5" aria-hidden />}
            onClick={onRebuildSed}
            disabled={rebuildDisabled}
            testId={`${testId}-rebuild`}
            spinning={rebuildActive}
          />
        )}
        {onDownloadSed && site.sedId && (
          <ActionCard
            title="Download SED"
            description="Export the current Site Evidence Document as JSON."
            icon={<Download className="w-3.5 h-3.5" aria-hidden />}
            onClick={onDownloadSed}
            testId={`${testId}-download`}
          />
        )}
        {onExportKMZ && (
          <ActionCard
            title="Export KMZ"
            description="Download the site boundary and layers as a KMZ file."
            icon={<Download className="w-3.5 h-3.5" aria-hidden />}
            onClick={onExportKMZ}
            disabled={!hasGeometry}
            titleAttr={!hasGeometry ? 'Draw or import a boundary first.' : undefined}
            testId={`${testId}-export-kmz`}
          />
        )}
        {onDeleteSed && site.sedId && (
          <ActionCard
            title={isDeletingSed ? 'Deleting…' : 'Delete SED'}
            description="Remove the SED and reset evidence state for this site."
            icon={<Trash2 className="w-3.5 h-3.5" aria-hidden />}
            onClick={onDeleteSed}
            disabled={isDeletingSed || rebuildActive}
            titleAttr={rebuildActive ? 'Rebuild in progress — wait before deleting.' : undefined}
            testId={`${testId}-delete`}
            tone="danger"
          />
        )}
      </div>
    </div>
  );
};
